'use client';

import { useState, useEffect } from 'react';

export interface RegionPricing {
  isIndia: boolean;
  currency: string;
  currencySymbol: string;
  /** One-time publish price (display value) */
  price: number;
  /** Original / strikethrough price */
  originalPrice: number;
  /** Discounted price (with launch offer) */
  discountedPrice: number;
  /** Formatted price string e.g. "₹1,500" or "$25" */
  priceText: string;
  /** Formatted original price string */
  originalPriceText: string;
  /** Formatted discounted price string */
  discountedPriceText: string;
  /** Formatted savings string */
  savingsText: string;
  /** "Free" in regional format */
  freeText: string;
  /** Whether detection is complete */
  ready: boolean;
}

const INR_PRICING = {
  currency: 'INR',
  currencySymbol: '₹',
  price: 1500,
  originalPrice: 2999,
  discountedPrice: 1499,
};

const USD_PRICING = {
  currency: 'USD',
  currencySymbol: '$',
  price: 25,
  originalPrice: 49,
  discountedPrice: 25,
};

function formatPrice(amount: number, currencySymbol: string, currency: string): string {
  if (currency === 'INR') {
    return `${currencySymbol}${amount.toLocaleString('en-IN')}`;
  }
  return `${currencySymbol}${amount.toLocaleString('en-US')}`;
}

/**
 * Detect if the user is in India using a 3-layer approach:
 *
 * 1. ?region= query param override (for testing - e.g. ?region=US or ?region=IN)
 * 2. x-user-country cookie set by middleware (Vercel geo headers - most accurate, VPN-aware)
 * 3. Timezone fallback (works offline / local dev)
 */
function detectIsIndia(): boolean {
  if (typeof window === 'undefined') return true; // SSR default to India

  // Layer 1: Query param override for testing
  try {
    const params = new URLSearchParams(window.location.search);
    const regionOverride = params.get('region');
    if (regionOverride) {
      return regionOverride.toUpperCase() === 'IN';
    }
  } catch {
    // continue to next layer
  }

  // Layer 2: Cookie from middleware (Vercel geo detection)
  try {
    const cookies = document.cookie.split(';').reduce((acc, c) => {
      const [key, val] = c.trim().split('=');
      if (key && val) acc[key] = val;
      return acc;
    }, {} as Record<string, string>);

    const country = cookies['x-user-country'];
    if (country) {
      return country === 'IN';
    }
  } catch {
    // continue to next layer
  }

  // Layer 3: Timezone fallback (local dev / no middleware)
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta') {
      return true;
    }
  } catch {
    // fallback below
  }

  // Layer 3b: Language hint
  try {
    const lang = navigator.language || '';
    if (lang.startsWith('hi') || lang === 'en-IN') {
      return true;
    }
  } catch {
    // fallback
  }

  return false;
}

export function useRegionPricing(): RegionPricing {
  const [isIndia, setIsIndia] = useState(true); // default India for SSR
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const detected = detectIsIndia();
    setIsIndia(detected);
    setReady(true);
  }, []);

  const p = isIndia ? INR_PRICING : USD_PRICING;

  return {
    isIndia,
    currency: p.currency,
    currencySymbol: p.currencySymbol,
    price: p.price,
    originalPrice: p.originalPrice,
    discountedPrice: p.discountedPrice,
    priceText: formatPrice(p.price, p.currencySymbol, p.currency),
    originalPriceText: formatPrice(p.originalPrice, p.currencySymbol, p.currency),
    discountedPriceText: formatPrice(p.discountedPrice, p.currencySymbol, p.currency),
    savingsText: formatPrice(p.originalPrice - p.discountedPrice, p.currencySymbol, p.currency),
    freeText: isIndia ? '₹0' : '$0',
    ready,
  };
}

/** Non-hook version for use in paymentConfig / server contexts */
export function getRegionPricingStatic(isIndia: boolean) {
  const p = isIndia ? INR_PRICING : USD_PRICING;
  return {
    ...p,
    priceText: formatPrice(p.price, p.currencySymbol, p.currency),
    originalPriceText: formatPrice(p.originalPrice, p.currencySymbol, p.currency),
    discountedPriceText: formatPrice(p.discountedPrice, p.currencySymbol, p.currency),
    savingsText: formatPrice(p.originalPrice - p.discountedPrice, p.currencySymbol, p.currency),
  };
}
